<template>
  <div class="page">
    <div class="page-toolbar bg-white shadow-sm p-3">
      <breadcrumb :items="breadCrumb" />
    </div>
    <div
      v-if="!loading"
      class="page-content table-builder p-3"
    >
      <ValidationObserver
        v-slot="{ handleSubmit }"
        ref="observer"
      >
        <b-form
          class=""
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <div class="row">
            <div class="col-sm-12 mb-3">
              <b-card
                no-body
                class="table-details"
              >
                <div slot="header">
                  <span>Table Details</span>
                </div>
                <b-card-body>
                  <div class="row">
                    <div class="col">
                      <input-widget
                        :model="tableModel"
                        :field="fields.displayName"
                      />
                    </div>
                    <div class="col">
                      <input-widget
                        :model="tableModel"
                        :field="fields.folderGuid"
                      />
                    </div>
                  </div>
                  <input-widget
                    :model="tableModel"
                    :field="fields.environmentVariableWithPowerAppsUrl"
                  />
                </b-card-body>
              </b-card>
            </div>
          </div>

          <div class="mt-3">
            <b-button
              type="submit"
              variant="primary"
              value="submit"
              class="mr-2"
            >
              <font-awesome-icon :icon="['far', 'save']" />
              Submit
            </b-button>
            <b-button
              type="button"
              variant="primary"
              value="submit"
              class="mr-2"
              @click="saveAndClose"
            >
              <font-awesome-icon :icon="['far', 'save']" />
              Save and Close
            </b-button>
            <b-button
              type="button"
              variant="danger"
              @click="cancelAction"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
              Cancel
            </b-button>
          </div>
        </b-form>
      </ValidationObserver>
    </div>

    <div v-else>
      <content-spinner :show="loading" />
    </div>
  </div>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import Breadcrumb from '../../../../core/components/Breadcrumb';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');

export default {
  name: 'TableBuilderPowerApps',
  components: {
    Breadcrumb,
    ContentSpinner,
    InputWidget,
  },
  data() {
    return {
      tableModel: {},
      tableFolder: {},
      fields: {
        displayName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'displayName',
        },
        environmentVariableWithPowerAppsUrl: {
          type: 'text',
          required: true,
          label: 'Power Apps Environment Variable Name',
          name: 'environmentVariableWithPowerAppsUrl',
        },
        folderGuid: {
          type: 'select',
          required: true,
          label: 'Folder',
          name: 'folderGuid',
          data: {},
        },
      },
      loading: false,
    };
  },
  computed: {
    ...mapState({
      currentTable: state => state.tableData.data,
      powerAppsEntity: state => state.powerAppsEntity,
      moduleFolders: state => state.folders.data,
    }),
    isEditMode() {
      return !!this.$route.params.tableUuid;
    },
  },
  methods: {
    ...mapActions([
      'getTableData',
      'getFolders',
      'getPowerAppsEntity',
      'createPowerAppsEntity',
      'getTables',
      'updatePowerAppsEntity',
    ]),
    async onSubmit(redirectToModule = false) {

      if (!await this.$refs.observer.validate()) {
        return;
      }

      this.loading = true;

      const params = {
        ...this.tableModel,
        moduleGuid: this.tableFolder.module.moduleGuid,
      };

      let response;
      if (this.isEditMode) {
        response = await this.updatePowerAppsEntity({
          entityGuid: this.currentTable.entityGuid,
          displayName: this.tableModel.displayName,
          environmentVariableWithPowerAppsUrl: this.tableModel.environmentVariableWithPowerAppsUrl,
        });
      } else {
        response = await this.createPowerAppsEntity({
          ...this.tableModel,
          moduleGuid: this.tableFolder.module.moduleGuid,
        });
      }

      if (response.success) {
        this.$toasted.success(`Table "${params.displayName}" was saved`, {
          duration: TOASTER_DURATION,
        });

        if (redirectToModule) {
          await this.$router.push({
            name: 'dataManagementPowerAppsTableView',
            params: {
              tableId: this.$route.params.tableUuid || response.body.entityGuid,
              moduleUuid: params.moduleGuid,
            },
          });
        }
        await this.getTables();
      } else {
        const errorMessage = response.body.ErrorMessage;
        this.$toasted.error(errorMessage, {
          duration: TOASTER_DURATION,
        });
      }

      this.loading = false;
    },
    async saveAndClose() {
      if (!await this.$refs.observer.validate()) {
        return;
      }

      await this.onSubmit(true);
    },
    cancelAction() {
      this.$router.go(-1);
    },
    async getData() {
      await this.getFolders(this.$route.params.moduleUuid);
      if (this.isEditMode) {
        await this.getTableData(this.$route.params.tableUuid);
        //todo: need ro test this functionality (was removed on #1560_Power_bi)
        // const {success, body} = await this.getPowerAppsEntity(this.currentTable.entityGuid);
        // if (!success) {
        //   this.$toasted.error(body.ErrorMessage, {
        //     duration: TOASTER_DURATION,
        //   });
      }
    },
    prepareData() {
      this.prepareFolderFieldOptions();
      this.getCurrentTableFolder();
      if (this.isEditMode) {
        this.prepareTableModel();
      }
    },
    prepareFolderFieldOptions() {
      this.fields.folderGuid.data = {
        options: this.moduleFolders.map(f => ({text: f.folderName, value: f.folderGuid})),
      };
    },
    getCurrentTableFolder() {
      const folderGuid = this.$route.query.folderUuid || this.currentTable.folderGuid;
      this.tableFolder = this.moduleFolders.find(folder => folder.folderGuid === folderGuid);
    },
    prepareTableModel() {
      this.tableModel = {
        'displayName': this.currentTable.displayName,
        'environmentVariableWithPowerAppsUrl': JSON.parse(this.currentTable.entityConfigJSON).EnvironmentVariableWithPowerAppsUrl,
        'moduleGuid': this.tableFolder.module.moduleGuid,
        'folderGuid': this.tableFolder.folderGuid,
      };
    },
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    this.prepareData();
    this.loading = false;
  },
};
</script>
